import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../components/ui'
import { artFor, isDeviceShot } from '../funnel/art'
import type { ValueNode } from '../funnel/flow'
import { useFunnel } from '../funnel/useFunnel'
import { ValueVisual } from './ValueVisual'
import { eventOnce, initialSessionId } from '../lib/api'

/**
 * The non-question beats: value reframes, and the two how-it-works screens.
 *
 * These exist to keep motivation up between question clusters and — for the
 * how-it-works pair — to be the only place a cold visitor actually sees what the
 * product is before being asked for anything.
 */
export function ValueScreen({ node }: { node: ValueNode }) {
  const { t } = useTranslation()
  const { answers, advance } = useFunnel()

  const variant = node.variantFor?.(answers)
  // Reassurance screens answer the barrier the visitor just named, so their copy
  // lives under a variant key; everything else is addressed by node id.
  const base = node.kind === 'howitworks' ? `howItWorks.${node.id}` : variant ? `value.${node.id}.variants.${variant}` : `value.${node.id}`
  const cta = variant ? t(`value.${node.id}.cta`) : t(`${base}.cta`)

  useEffect(() => {
    void eventOnce(initialSessionId, 'step_viewed', {
      step: node.id,
      nodeId: node.id,
      ...(variant ? { variant } : {}),
    }).catch(() => {})
  }, [node.id, variant])

  const art = artFor(node.art)
  // Device mockups already carry their own bezel; anything else is a loose
  // illustration and gets the sticker treatment.
  const deviceShot = isDeviceShot(node.art)

  return (
    <div className="content-column text-center">
      <p className="eyebrow">{t(`${base}.eyebrow`)}</p>
      <h1 className="value-title">{t(`${base}.title`)}</h1>
      <p className="body-copy">{t(`${base}.body`)}</p>
      {node.visual ? (
        <ValueVisual kind={node.visual} answers={answers} />
      ) : art ? (
        <img src={art} alt="" className={deviceShot ? 'app-shot' : 'value-art'} loading="lazy" />
      ) : null}
      <Button block size="lg" variant="sunshine" onClick={() => advance(node.id)}>
        {cta}
      </Button>
    </div>
  )
}
